<script setup>
import {computed, ref} from 'vue'
import {set} from 'vue'

const props = defineProps({
  value: Object,
  available: Object
})

// Local alias to the same object reference so we can mutate it in place
// (parent holds the same reference via v-model) without tripping vue/no-mutating-props.
const edit = props.value

// Ensure this is always a real array up front so b-form-checkbox can v-model
// directly against it (checkbox-group array binding requires a non-null array).
if (!edit.permissions)
  set(edit, 'permissions', [])

const search = ref('')
const expanded = ref([])

const role_perms = computed(() => new Set(props.available.roles[edit.role] || []))
const extra_perms = computed(() => new Set(edit.permissions || []))

const groups = computed(() => {
  const map = {}
  for (const perm of props.available.all || []) {
    const ns = perm.includes('.') ? perm.slice(0, perm.indexOf('.')) : perm
    if (!map[ns])
      map[ns] = []
    map[ns].push(perm)
  }

  return Object.keys(map).sort().map(ns => ({
    ns,
    perms: map[ns].sort(),
  }))
})

// Stats depend on role_perms/extra_perms, so this recomputes (cheaply) whenever a
// checkbox is toggled or the role changes - but each group's granted/total count is
// only computed once per change, not once per render per badge like a plain function would be.
const groups_with_stats = computed(() => groups.value.map(g => {
  const granted = g.perms.filter(p => role_perms.value.has(p) || extra_perms.value.has(p)).length
  return {ns: g.ns, perms: g.perms, granted, total: g.perms.length}
}))

const filtered_groups = computed(() => {
  const q = search.value.trim().toLowerCase()
  if (!q)
    return groups_with_stats.value

  return groups_with_stats.value
      .map(g => ({...g, perms: g.perms.filter(p => p.toLowerCase().includes(q))}))
      .filter(g => g.perms.length)
})

const is_expanded = (ns) => !!search.value.trim() || expanded.value.includes(ns)

const toggle_group = (ns) => {
  const idx = expanded.value.indexOf(ns)
  if (idx === -1)
    expanded.value.push(ns)
  else
    expanded.value.splice(idx, 1)
}

const is_role_perm = (perm) => role_perms.value.has(perm)

const select_all = (perms, on) => {
  perms.forEach(perm => {
    if (is_role_perm(perm))
      return

    const idx = edit.permissions.indexOf(perm)
    if (on && idx === -1)
      edit.permissions.push(perm)
    else if (!on && idx !== -1)
      edit.permissions.splice(idx, 1)
  })
}

const total_granted = computed(() => {
  const all = new Set([...role_perms.value, ...extra_perms.value])
  return all.size
})
</script>

<template>
  <div>
    <modal-row label="Role" even>
      <b-select :options="Object.keys(available.roles)" v-model="edit.role" size="sm"/>
    </modal-row>

    <div class="d-flex flex-row align-items-center my-2">
      <b-input-group size="sm" class="flex-fill">
        <b-input-group-prepend is-text>
          <font-awesome-icon :icon="['fas', 'magnifying-glass']"/>
        </b-input-group-prepend>
        <b-form-input v-model="search" placeholder="Search permissions..." autocomplete="off"/>
        <b-input-group-append v-if="search">
          <b-btn variant="outline-secondary" @click="search = ''">
            <font-awesome-icon :icon="['fas', 'xmark']"/>
          </b-btn>
        </b-input-group-append>
      </b-input-group>

      <div class="text-muted small ml-3 text-nowrap">
        {{ total_granted }} of {{ available.all.length }} granted
      </div>
    </div>

    <div class="overflow-auto border rounded" style="height: 50vh">
      <div v-for="g in filtered_groups" :key="g.ns" class="border-bottom">
        <div class="d-flex flex-row align-items-center px-2 py-1 cursor-pointer bg-light"
             @click="toggle_group(g.ns)">
          <font-awesome-icon class="mr-2 text-muted" style="width: 0.8em"
                              :icon="['fas', is_expanded(g.ns) ? 'chevron-down' : 'chevron-right']"/>
          <div class="flex-fill font-weight-bold text-capitalize">{{ g.ns }}</div>
          <b-badge :variant="g.granted ? 'primary' : 'secondary'" class="mr-2">
            {{ g.granted }} / {{ g.total }}
          </b-badge>
          <b-btn size="sm" variant="link" class="py-0" @click.stop="select_all(g.perms, true)">All</b-btn>
          <b-btn size="sm" variant="link" class="py-0" @click.stop="select_all(g.perms, false)">None</b-btn>
        </div>

        <b-row no-gutters class="px-3 py-2" v-if="is_expanded(g.ns)">
          <b-col md="6" lg="4" v-for="perm in g.perms" :key="perm" class="py-1">
            <b-form-checkbox v-if="is_role_perm(perm)" :checked="true" disabled>
              {{ perm.slice(g.ns.length + 1) || perm }}
              <b-badge variant="secondary" class="ml-1">role</b-badge>
            </b-form-checkbox>
            <b-form-checkbox v-else v-model="edit.permissions" :value="perm">
              {{ perm.slice(g.ns.length + 1) || perm }}
            </b-form-checkbox>
          </b-col>
        </b-row>
      </div>

      <div v-if="!filtered_groups.length" class="text-center text-muted p-4">
        No permissions match "{{ search }}"
      </div>
    </div>
  </div>
</template>

<style scoped>
</style>
